<script setup>
import { provide, ref, computed, inject, watch, nextTick, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { TvbSsoButton } from '@tvb-sz/tvb-headerfooter-frontend'
import { getArticleDetail, getArticlePreview, postArticleLike } from '@/api/article.js'
import { useElementVisibility, useElementBounding } from '@vueuse/core'
import { useHead } from '@unhead/vue'
import { toISOString, appendUtmParams } from '@/utils'
import { pageview, popUp, action } from '@/utils/gtm'
import { sha1 } from 'js-sha1'
import useArticle from '../Detail/components/Article/useArticle'
import VoteMain from './components/Main/index.vue'
import Subtitle from '../Detail/components/Subtitle/index.vue'
import leftTool from '../Detail/components/ArticleTool/leftTool.vue'
import bottomTool from '../Detail/components/ArticleTool/bottomTool.vue'
import previewLogin from '../components/PreviewLogin/index.vue'
import { getSessionStorage } from '@/utils'
import { Head } from '@unhead/vue/components'

const route = useRoute()
const router = useRouter()
const isPreview = inject('isPreview')
const perviewLogin = ref(false)
const perviewLoginKey = ref(false)
const isPreviewSecret = ref(false)
const size = inject('size')
const seoTitleSuffix = inject('seoTitleSuffix')
const layerData = inject('layerData')
const isMobile = computed(() => ['s', 'xs'].includes(size.value))
const pollingId = computed(() => (route?.params?.pollingId || '').replace('p', ''))
const loginRef = ref()

const {
  delayShow,
  breadcrumbs,
  categoryInfo,
  cloading,
  fetchCategoryInfo,
  eloading,
  fetchExtraInfo
} = useArticle()

const info = ref()
const loading = ref(true)

// seo - start
const VITE_HOST_URL = import.meta.env.VITE_HOST_URL
const canonical = computed(() => VITE_HOST_URL + (info?.value?.uri?.slice(1) || ''))
const seoTitle = computed(() => (info.value?.seo_title || info.value?.title) + seoTitleSuffix)
const seoAuthor = computed(() => info.value?.author?.join(',') || 'TVB')
const seoImageUrl = computed(
  () => info.value?.og_image_info?.large || info.value?.image_info?.large || ''
)
const seoImageAlt = computed(() => info.value?.og_image_alt_txt || '')
const seoDescription = computed(() => info.value?.meta_description || '')
const ldJsonScript = computed(() => {
  if (!info.value || !categoryInfo.value) {
    return
  }
  let title = (info.value?.seo_title || info.value?.title) + seoTitleSuffix
  let ogImage = info.value?.og_image_info?.large || info.value?.image_info?.large || ''
  let articleData = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    image: {
      '@type': 'ImageObject',
      contentUrl: ogImage,
      creditText: 'TVB 無綫集團',
      creator: {
        '@type': 'Organization',
        name: 'TVB 無綫集團'
      }
    },
    headline: title,
    datePublished: toISOString(info.value.publish_at),
    dateModified: toISOString(info.value.updated_at),
    isAccessibleForFree: 'true',
    author: {
      '@type': 'Organization',
      name: 'TVB 無綫集團',
      url: location.origin
    },
    publisher: {
      name: 'TVB 無綫集團',
      url: location.origin
    },
    mainEntityOfPage: canonical.value,
    description: info.value.meta_description,
    articleSection: [categoryInfo.value.parent.title, categoryInfo.value.title],
    keywords: []
  }

  const BreadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      ...breadcrumbs.value.map((v, i) => {
        return {
          '@type': 'ListItem',
          position: i + 1,
          name: v.name,
          item: v.path == '/' ? location.origin : v.path
        }
      }),
      {
        '@type': 'ListItem',
        position: 4,
        name: info.value.title,
        item: canonical.value
      }
    ]
  }
  return JSON.stringify([articleData, BreadcrumbList])
})
const scriptData = computed(() => ({
  link: [
    {
      rel: 'canonical',
      href: canonical.value
    },
    {
      rel: 'alternate',
      hreflang: 'x-default',
      href: canonical.value
    },
    {
      rel: 'alternate',
      hreflang: 'zh-hk',
      href: canonical.value
    }
  ],
  script: [{ id: 'vote-ld-json', innerHTML: ldJsonScript.value, type: 'application/ld+json' }]
}))
const { patch } = useHead(scriptData)
// seo -end
const fetchArticleInfo = async () => {
  try {
    const extraParams = {}
    if (isPreview.value) {
      if (!import.meta.env.SSR) {
        const pwd = getSessionStorage(pollingId.value)
        isPreviewSecret.value = !!pwd
        if (pwd) {
          extraParams.pwd = pwd
        }
      }
    }

    const apiFun = isPreview.value ? getArticlePreview : getArticleDetail
    const res = await apiFun({ article_id: pollingId.value, ...extraParams })

    if (isPreview.value && res.code === 103023) {
      if (!import.meta.env.SSR) return (window.location = '/403.html')
    }
    if (isPreview.value && res.code === 203043) {
      perviewLogin.value = true
      perviewLoginKey.value = !perviewLoginKey.value
    } else {
      perviewLogin.value = false
    }
    if (res.code !== 0) throw new Error(res.message)
    // console.log('详情--', res.data)
    info.value = res.data
    if (isPreview.value && info.value.source != 3) {
      router.replace(`/preview/article/${pollingId.value}`)
    }
    fetchCategoryInfo(res.data.category_id)

    const extraInfoParams = { content_type: res.data.content_type }
    if (isPreview.value) {
      extraInfoParams['key'] = route?.params?.pollingId
    }
    fetchExtraInfo(res.data.id, extraInfoParams, extraCallback)
    loading.value = false
  } catch (error) {
    console.warn(error)
    loading.value = false
  }
}
fetchArticleInfo()

const extraInfo = ref({})
const is_like = ref(false)
const like_num = ref(0)
const extraCallback = (data) => {
  extraInfo.value = data
  is_like.value = data?.is_like
  like_num.value = data?.like_num || 0
  if (isPreview.value) return
  nextTick(() => {
    if (canonical.value)
      // TC-1631: 保留 utm 参数在地址栏
      window.history.replaceState(
        history.state,
        null,
        appendUtmParams(canonical.value) + location.hash
      )
    patch(scriptData.value)
  })
}

// 刷新状态
// const refreshData = (callback) => {
//   fetchExtraInfo(info.value.id, (data) => {
//     extraInfo.value = data
//     callback && callback()
//   })
// }

// 处理点赞
const onClickLike = async () => {
  try {
    const res = await postArticleLike({
      type: 1,
      liked_id: info.value.id,
      status: is_like.value ? 2 : 1
    })
    if (res.code === 150001) {
      loginRef.value?.login && loginRef.value.login()
      throw new Error(res.message)
    }
    if (res.code !== 0) throw new Error(res.message)
    is_like.value = !is_like.value
    like_num.value = is_like.value ? like_num.value + 1 : like_num.value - 1
  } catch (error) {
    console.warn(error)
  }
}

const dividerRef = ref()
const targetIsVisible = useElementVisibility(dividerRef)
const elementBounding = useElementBounding(dividerRef)

const sendPV = (params = {}) => {
  pageview({
    ...layerData.value,
    page_title: 'vote_page',
    screen_class: info.value.title || '',
    eventCategory: 'sub_category',
    eventLabel: 'article',
    eventID: `p${info.value.id}`,
    screenLabel: canonical.value + location.hash,
    ...params
  })
}

watch(
  [cloading, eloading],
  () => {
    if (!eloading.value && !cloading.value && !location.hash) {
      let vid = extraInfo.value?.voting_data?.voting_list?.[0]?.voting_id
      sendPV({ eventID: `p${info.value.id}${vid ? '_' + vid : ''}` })
    }
  },
  { flush: 'post' }
)

const onViewerShown = (pswp) => {
  const index = pswp?.currIndex
  popUp({
    ...layerData.value,
    eventID: 'o003',
    page_title: 'popUp',
    eventLabel: `${categoryInfo.value.title || ''}_popup`,
    screen_class: info.value.title || '',
    eventCategoy: 'sub_category',
    screenLabel: canonical.value + location.hash,
    listName: index + 1
  })
  nextTick(() => {
    const slide = pswp?.currSlide
    pageview({
      screenID: 'S006',
      page_title: 'album_view',
      eventLabel: 'album_view',
      screen_class: info.value.title || '',
      eventCategoy: 'sub_category',
      eventID: categoryInfo.value.id || '',
      screenLabel: canonical.value + '?' + sha1(slide?.data.src) + location.hash,
      photo_id: sha1(slide?.data.src)
    })
  })
}

const onViewerChange = (pswp) => {
  action({
    ...layerData.value,
    eventID: 'B021',
    page_title: 'album_popup',
    eventLabel: 'album_popup',
    screen_class: info.value.title || '',
    eventCategoy: 'sub_category',
    screenLabel: canonical.value + location.hash,
    photo_id: sha1(pswp?.currSlide?.data.src)
  })
}

provide('activityInfo', info)
provide('extraInfo', extraInfo)
// provide('refreshData', refreshData)
provide('loginRef', loginRef)
provide('sendPV', sendPV)
onMounted(() => {
  // document.addEventListener('contextmenu', (e) =>{
  //   e.preventDefault()
  // })
  // document.addEventListener('dragstart', (e) =>{
  //   e.preventDefault()
  // })
})
</script>

<template>
  <Head>
    <title>{{ seoTitle }}</title>
    <meta name="author" :content="seoAuthor" />
    <meta name="description" :content="seoDescription" />
    <meta property="og:type" content="article" />
    <meta property="og:image" :content="seoImageUrl" />
    <meta property="og:title" :content="seoTitle" />
    <meta property="og:image:alt" :content="seoImageAlt" />
    <meta property="og:url" :content="canonical" />
    <meta property="og:description" :content="seoDescription" />
    <meta name="twitter:title" :content="seoTitle" />
    <meta name="twitter:image" :content="seoImageUrl" />
    <meta name="twitter:image:alt" :content="seoImageAlt" />
    <meta name="twitter:url" :content="canonical" />
    <meta property="twitter:description" :content="seoDescription" />
  </Head>
  <div v-if="loading || !info" class="init-page" :class="{ 'error-page': !loading && !info }">
    <div v-if="loading" class="tvb-loading-box init-loading-box"><n-spin /></div>
    <preview-login
      v-else-if="isPreview && perviewLogin"
      :key="perviewLoginKey"
      :value="pollingId"
      :pwd-error="isPreviewSecret"
      :query-fun="() => fetchArticleInfo()"
    ></preview-login>
    <tvb-empty
      v-else
      icon="img_article_deleted"
      :des="isPreview ? '預覽鏈接已失效，請重新獲取' : '抱歉，找不到相關資料'"
      :showbtn="!isPreview"
    ></tvb-empty>
  </div>
  <template v-else>
    <div class="tvb-article-detail disable-content-copy">
      <div class="tvb-article-detail-wrap">
        <div
          v-show="categoryInfo && !cloading"
          class="tvb-article-breadcrumb"
          :class="{ 'tvb-preview relative': isPreview }"
        >
          <tvb-breadcrumb
            :breadcrumbs="breadcrumbs"
            :size="size"
            :loading="cloading"
          ></tvb-breadcrumb>
        </div>

        <div class="tvb-article-wrap" :class="'affix-container-wrap-' + info.id">
          <h1 class="clear-h tvb-article-title" :title="info.title">{{ info.title }}</h1>
          <template v-if="!isMobile && !isPreview">
            <left-tool
              :share-id="info.id"
              :size="size"
              :is-like="is_like"
              :like-num="like_num"
              @like="onClickLike"
            ></left-tool>
          </template>
          <subtitle :info="info" :size="size" />

          <vote-main
            :loading="eloading"
            @viewerShown="onViewerShown"
            @viewerChange="onViewerChange"
          />

          <transition name="tvb-slide-up">
            <bottom-tool
              v-if="isMobile && delayShow"
              v-show="!targetIsVisible && elementBounding.bottom.value > 10"
              :share-id="info.id"
              :size="size"
              :is-like="is_like"
              :like-num="like_num"
              :show-btn="!isPreview"
              @like="onClickLike"
            ></bottom-tool>
          </transition>
        </div>
      </div>

      <div ref="dividerRef" style="height: 0.5px"></div>
      <tvb-sso-button type="login-logout" ref="loginRef" style="display: none" />
    </div>
  </template>
</template>

<style lang="scss" scoped>
.init-page {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  .init-loading-box {
    padding: 32px 0;
  }
}

.error-page {
  display: flex;
  align-items: center;
  justify-content: center;
  .tvb-empty {
    height: 500px;
  }
  &.s,
  &.xs {
    align-items: stretch;
    padding-top: 100px !important;
    padding-bottom: 100px !important;
    .tvb-empty {
      height: 400px;
    }
  }
}

.tvb-article-detail {
  padding-top: 16px !important;
  &.s,
  &.xs {
    padding-top: 10px !important;
    .tvb-article-breadcrumb {
      margin-bottom: 16px;
    }
    .tvb-article-title {
      font-size: 22px;
      line-height: 28px;
      margin-bottom: 10px;
      color: #333;
    }
  }

  .tvb-article-detail-wrap {
    max-width: var(--tvb-article-max-width);
    margin: 0 auto;
  }

  .tvb-article-breadcrumb {
    margin-bottom: 32px;
  }

  .tvb-article-wrap {
    position: relative;
  }

  .tvb-article-title {
    font-size: 28px;
    font-weight: 500;
    color: #1e1e1e;
    line-height: 34px;
    letter-spacing: 0.5px;
    margin-bottom: 16px;
  }
}
</style>
